import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  trackRegistrationStart,
  trackRegistrationError,
  trackSignupComplete,
  getAttributionParams,
} from "../utils/analytics";
import neuBirdLogo from "../assets/NeuBird-Horizontal-Default.svg";
import contextMapImg from "../assets/context-map.png";

const PASSWORD_RULES = [
  { key: "length", label: "At least 8 characters", test: (pw) => pw.length >= 8 },
  { key: "lower", label: "Lower case letters (a-z)", test: (pw) => /[a-z]/.test(pw) },
  { key: "upper", label: "Upper case letters (A-Z)", test: (pw) => /[A-Z]/.test(pw) },
  { key: "number", label: "Numbers (0-9)", test: (pw) => /[0-9]/.test(pw) },
  { key: "special", label: "Special characters (e.g. !@#$%^&*)", test: (pw) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pw) },
];

const RegistrationForm = ({ marketplaceToken }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  useEffect(() => {
    trackRegistrationStart();
  }, []);

  const API_URL = process.env.REACT_APP_API_URL || "";
  const DEV_MODE = process.env.REACT_APP_DEV_MODE === "true";

  const validatePassword = (pw) => {
    const lengthOk = PASSWORD_RULES[0].test(pw);
    const categoryPasses = PASSWORD_RULES.slice(1).filter((r) => r.test(pw)).length;
    return lengthOk && categoryPasses >= 3;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.company.trim()) newErrors.company = "Company name is required";

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (formData.email.trim().length > 254) {
      newErrors.email = "Email must be 254 characters or less";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    } else {
      const emailDomain = formData.email.trim().split('@')[1]?.toLowerCase();
      const personalEmailDomains = [
        'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
        'icloud.com', 'mail.com', 'protonmail.com', 'yandex.com', 'zoho.com',
        'gmx.com', 'live.com', 'msn.com', 'me.com', 'mac.com'
      ];
      if (emailDomain && personalEmailDomains.includes(emailDomain)) {
        newErrors.email = "Please use your work email address, not a personal email";
      }
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!validatePassword(formData.password)) {
      newErrors.password = "Password does not meet the requirements";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setSubmitError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      trackRegistrationError("validation_failed");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const utmParams = getAttributionParams();
      const payload = {
        token: marketplaceToken || null,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        company: formData.company.trim(),
        email: formData.email.trim(),
        password: formData.password,
        ...(utmParams.utm_source && { utm_source: utmParams.utm_source }),
        ...(utmParams.utm_medium && { utm_medium: utmParams.utm_medium }),
        ...(utmParams.utm_campaign && { utm_campaign: utmParams.utm_campaign }),
        ...(utmParams.utm_content && { utm_content: utmParams.utm_content }),
      };

      const response = await axios.post(`${API_URL}/earlybird/register`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success) {
        trackSignupComplete({ method: 'direct_website' });
        if (response.data.registration_id) {
          localStorage.setItem("earlybird_registration_id", response.data.registration_id);
          navigate("/registrations/provisioning");
        } else {
          navigate("/registrations/complete");
        }
      } else {
        const errorMsg = response.data.error || "Registration failed. Please try again.";
        trackRegistrationError("api_error");
        setSubmitError(errorMsg);
      }
    } catch (error) {
      console.error("Registration error:", error);
      let errorMsg = "Registration failed. Please try again.";
      if (error.response && error.response.data) {
        errorMsg = error.response.data.error || error.response.data.message || errorMsg;
      }
      trackRegistrationError("network_error");
      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const pw = formData.password;
  const categoryPasses = PASSWORD_RULES.slice(1).filter((r) => r.test(pw)).length;
  const emailEntered = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());

  return (
    <>
      <nav className="site-nav">
        <a className="nav-logo" href="https://neubird.ai">
          <img src={neuBirdLogo} alt="NeuBird AI" />
        </a>
        <span className="nav-cta"></span>
      </nav>

      <main className="page">
        <section className="left">
          <div className="icp-badge">
            For Startups &middot; Free Credits Included
          </div>

          <h1>Introducing <em>NewBird</em>.</h1>
          <p className="icp-qualifier">
            The NewBird program by NeuBird gives startups free credits and daily operational
            intelligence for your AWS or Azure environments. AI-powered reports delivered to your
            inbox with actionable insights to keep your infrastructure running at its best — so
            your team can focus on building.
          </p>

          <div className="demo-visual">
            <img
              src={contextMapImg}
              alt="NewBird — AI-powered operational intelligence for startups"
              className="demo-image"
            />
          </div>

          <div className="features">
            <div className="feature">
              <div className="feature-head">Free NewBird Credits</div>
              <div className="feature-body">
                Get free credits to use across the NeuBird platform. Investigate incidents, run analyses, and automate operations at no cost.
              </div>
              <div className="feature-metric">Free credits &middot; No commitment</div>
            </div>
            <div className="feature">
              <div className="feature-head">Daily Predictive Risk Report</div>
              <div className="feature-body">
                Anticipate issues before they impact your services. Surface emerging risks across your infrastructure daily.
              </div>
              <div className="feature-metric">Proactive &middot; Delivered every morning</div>
            </div>
            <div className="feature">
              <div className="feature-head">Daily Cost Optimization Report</div>
              <div className="feature-body">
                Identify savings across your cloud spend. Get actionable recommendations to reduce waste and right-size resources.
              </div>
              <div className="feature-metric">Savings identified automatically</div>
            </div>
            <div className="feature">
              <div className="feature-head">Daily Security &amp; Observability Reports</div>
              <div className="feature-body">
                Track coverage gaps, surface vulnerabilities, and monitor compliance risks. Stay ahead with daily assessments.
              </div>
              <div className="feature-metric">Security &middot; Observability &middot; Compliance</div>
            </div>
          </div>
        </section>

        <aside className="right">
          <div className="form-eyebrow">Free for Startups.</div>

          <div className="trial-promise">
            <div className="tp-title">What you'll get</div>
            <div className="tp-steps">
              <div className="tp-step">
                <div className="tp-num">1</div>
                <div className="tp-text">
                  Free NewBird credits to get started{" "}
                  <span className="tp-time">instant</span>
                </div>
              </div>
              <div className="tp-step">
                <div className="tp-num">2</div>
                <div className="tp-text">
                  Daily reports for your AWS or Azure environments{" "}
                  <span className="tp-time">every morning</span>
                </div>
              </div>
              <div className="tp-step">
                <div className="tp-num">3</div>
                <div className="tp-text">
                  AI-powered risk, cost, security &amp; observability insights{" "}
                  <span className="tp-time">actionable</span>
                </div>
              </div>
            </div>
          </div>

          {DEV_MODE && (
            <div className="info-box" style={{ marginBottom: "1rem", backgroundColor: "#fff3cd", border: "1px solid #ffc107" }}>
              <p><strong>Development Mode:</strong> Validation bypassed for testing.</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="firstName">First Name<span className="req">*</span></label>
                <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} className={errors.firstName ? "error" : ""} placeholder="Sarah" disabled={isSubmitting} autoComplete="given-name" />
                <div className="error-message">{errors.firstName || ""}</div>
              </div>

              <div className="field">
                <label htmlFor="lastName">Last Name<span className="req">*</span></label>
                <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} className={errors.lastName ? "error" : ""} placeholder="Chen" disabled={isSubmitting} autoComplete="family-name" />
                <div className="error-message">{errors.lastName || ""}</div>
              </div>

              <div className="field full">
                <label htmlFor="company">Company Name<span className="req">*</span></label>
                <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className={errors.company ? "error" : ""} placeholder="Acme Corp" disabled={isSubmitting} autoComplete="organization" />
                <span className="field-hint">Used to set up your NewBird workspace</span>
                <div className="error-message">{errors.company || ""}</div>
              </div>

              <div className="field full">
                <label htmlFor="email">Work Email<span className="req">*</span></label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={errors.email ? "error" : ""} placeholder="sarah@acmecorp.com" disabled={isSubmitting} autoComplete="email" maxLength={254} />
                <span className="field-hint">We'll deliver your daily reports here</span>
                <div className="error-message">{errors.email || ""}</div>
              </div>

              {emailEntered && (
              <>
              <div className="field full">
                <label htmlFor="password">Password<span className="req">*</span></label>
                <div className="password-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    className={errors.password ? "error" : ""}
                    placeholder="Create a password"
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />
                  <button type="button" className="pw-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                {(passwordFocused || pw.length > 0) && (
                  <div className="pw-requirements">
                    <div className={`pw-rule ${PASSWORD_RULES[0].test(pw) ? "pass" : ""}`}>
                      {PASSWORD_RULES[0].test(pw) ? "\u2713" : "\u2022"} {PASSWORD_RULES[0].label}
                    </div>
                    <div className={`pw-rule ${categoryPasses >= 3 ? "pass" : ""}`}>
                      {categoryPasses >= 3 ? "\u2713" : "\u2022"} At least 3 of the following:
                    </div>
                    {PASSWORD_RULES.slice(1).map((rule) => (
                      <div key={rule.key} className={`pw-rule pw-sub ${rule.test(pw) ? "pass" : ""}`}>
                        {rule.test(pw) ? "\u2713" : "\u2022"} {rule.label}
                      </div>
                    ))}
                  </div>
                )}
                <div className="error-message">{errors.password || ""}</div>
              </div>

              <div className="field full">
                <label htmlFor="confirmPassword">Confirm Password<span className="req">*</span></label>
                <div className="password-wrapper">
                  <input
                    type={showConfirm ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={errors.confirmPassword ? "error" : ""}
                    placeholder="Confirm your password"
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />
                  <button type="button" className="pw-toggle" onClick={() => setShowConfirm(!showConfirm)} tabIndex={-1}>
                    {showConfirm ? "Hide" : "Show"}
                  </button>
                </div>
                <div className="error-message">{errors.confirmPassword || ""}</div>
              </div>
              </>
              )}
            </div>

            <div className="security-row">
              <div className="sec-badge">Read-only access</div>
              <div className="sec-badge">SOC 2 Type II</div>
              <div className="sec-badge">SSO / SAML</div>
              <div className="sec-badge">Data encrypted</div>
            </div>

            {submitError && (
              <div className="error-message" style={{ marginBottom: "1rem", textAlign: "center", visibility: "visible" }}>
                {submitError}
              </div>
            )}

            <button type="submit" className="cta-btn" disabled={isSubmitting}>
              {isSubmitting ? (<><span className="loading">⏳</span>Starting...</>) : "Get Started Free →"}
            </button>
            <p className="cta-meta">
              <strong>Free for startups</strong> &middot; No credit card &middot; Free credits included
            </p>
            <p className="privacy-line">
              By signing up, you agree to our{" "}
              <a href="https://neubird.ai/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{" "}
              and{" "}
              <a href="https://neubird.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
          </form>
        </aside>
      </main>

      <footer className="site-footer">
        <span className="footer-text">&copy; 2026 NeuBird AI, Inc.</span>
        <div className="footer-links">
          <a href="https://neubird.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
          <a href="https://neubird.ai/terms" target="_blank" rel="noopener noreferrer">Terms</a>
          <a href="https://neubird.ai/security" target="_blank" rel="noopener noreferrer">Security</a>
          <a href="https://neubird.ai/contact" target="_blank" rel="noopener noreferrer">Contact</a>
        </div>
      </footer>
    </>
  );
};

export default RegistrationForm;
